import React, { ComponentProps, useCallback, useMemo, useState } from 'react'
import { Fade, Stack } from '@mui/material'
import Tabs from '../../shared/components/Tabs/Tabs'
import { useStrategy } from '../../shared/hooks/useStrategy'
import useValueMessageDisplayName from '../../shared/hooks/useDisplayNames/variants/useValueMessageDisplayName'
import useValueNarrativeDisplayName from '../../shared/hooks/useDisplayNames/variants/useValueNarrativeDisplayName'
import useActivitiesDisplayName from '../../shared/hooks/useDisplayNames/variants/useActivitiesDisplayName'
import useEvidenceDisplayName from '../../shared/hooks/useDisplayNames/variants/useEvidenceDisplayName'
import useStrategicImperativeDisplayName from '../../shared/hooks/useDisplayNames/variants/useStrategicImperativeDisplayName'
import useTppDisplayName from '../../shared/hooks/useDisplayNames/variants/useTppDisplayName'
import { useCommentsViewFiltersStorage } from '../../shared/hooks/useCommentsViewFiltersStorage'
import { useUpdateCommentsViewCommentMutation } from './mutations/useUpdateCommentsViewCommentMutation'
import { useGetCommentsDashboardQuery } from './queries/useGetCommentsDashboardQuery'
import { CommentsViewComment, CommentsViewTabId } from './CommentsView.types'
import { getAcronym } from '../../shared/utils/acronym'
import useActivityStatuses from '../../shared/hooks/useActivityStatuses'
import { getCommentsDashboardDomainFilterDefinitions, getCommentsDashboardDomainTabId } from './CommentsView.filters'
import { useCommentsViewFilters } from './useCommentsViewFilters'
import { normalizeCommentsViewFiltersState, SortState, toHiddenCommentsColumnIds } from './CommentsView.helpers'
import { useCommentsViewDerivedData } from './useCommentsViewDerivedData'
import { useCommentsViewTabs } from './useCommentsViewTabs'
import { useTableRowRestore } from '../../shared/hooks/useTableRowRestore'
import CommentsTableContent from './CommentsTableContent'

const CommentsView = ({
    selectedTabId: controlledSelectedTabId,
    onTabChange,
}: {
    selectedTabId?: CommentsViewTabId
    onTabChange?: (tabId: CommentsViewTabId) => void
} = {}) => {
    const strategy = useStrategy()

    const strategicImperativeDisplayName = useStrategicImperativeDisplayName()
    const valueMessageDisplayName = useValueMessageDisplayName()
    const valueNarrativeDisplayName = useValueNarrativeDisplayName()
    const activitiesDisplayName = useActivitiesDisplayName()
    const evidenceDisplayName = useEvidenceDisplayName()
    const tppDisplayName = useTppDisplayName()
    const { getActivityStatusDetails } = useActivityStatuses()
    const commentsQuery = useGetCommentsDashboardQuery(strategy?._id)
    const updateCommentMutation = useUpdateCommentsViewCommentMutation(strategy?._id)

    const { restoreState: parentRestoreState } = useTableRowRestore<SortState>('commentsView')

    const [uncontrolledSelectedTabId, setUncontrolledSelectedTabId] = useState<CommentsViewTabId>('all')
    const [searchQuery, setSearchQuery] = useState(parentRestoreState?.searchQuery ?? '')
    const [savingCommentIds, setSavingCommentIds] = useState<Set<string>>(new Set())
    const selectedTabId = controlledSelectedTabId ?? uncontrolledSelectedTabId

    const strategyTitle = [strategy?.product, strategy?.indication].filter(Boolean).join(' for ') || 'Comments'
    const tppAcronymDisplayName = getAcronym(tppDisplayName.singular)
    const comments = useMemo(() => commentsQuery.data ?? [], [commentsQuery.data])
    const isCommentsLoading = commentsQuery.isLoading || (!commentsQuery.isFetched && !commentsQuery.isError)
    const { filters, updateFilters, updateHiddenColumns } = useCommentsViewFiltersStorage()
    const hiddenColumnIds = useMemo(
        () => toHiddenCommentsColumnIds(filters?.hiddenColumnIds ?? []),
        [filters?.hiddenColumnIds]
    )
    const filtersState = useMemo(
        () => normalizeCommentsViewFiltersState(filters?.filtersState),
        [filters?.filtersState]
    )

    // Tab config and labels — extracted to a dedicated hook to keep this component lean.
    const { tabs: commentsViewTabs, domainTabLabels } = useCommentsViewTabs({
        strategicImperativeDisplayName,
        valueMessageDisplayName,
        valueNarrativeDisplayName,
        activitiesDisplayName,
        evidenceDisplayName,
        tppAcronymDisplayName,
    })

    const domainFilterDefinitions = useMemo(
        () =>
            getCommentsDashboardDomainFilterDefinitions({
                getActivityStatusLabel: (statusId) => getActivityStatusDetails(statusId).name,
                getActivityStatusOrder: (statusId) => getActivityStatusDetails(statusId).order,
            }),
        [getActivityStatusDetails]
    )

    const addSavingCommentId = useCallback((commentId: string) => {
        setSavingCommentIds((prev) => new Set(prev).add(commentId))
    }, [])

    const removeSavingCommentId = useCallback((commentId: string) => {
        setSavingCommentIds((prev) => {
            const next = new Set(prev)
            next.delete(commentId)
            return next
        })
    }, [])

    const isCommentSaving = useCallback((commentId: string) => savingCommentIds.has(commentId), [savingCommentIds])

    const handleUpdateComment = useCallback(
        async (comment: CommentsViewComment, updates: { action?: string; isResolved?: boolean }) => {
            addSavingCommentId(comment._id)

            try {
                await updateCommentMutation.mutateAsync({
                    commentId: comment._id,
                    domainTabId: getCommentsDashboardDomainTabId(comment) ?? undefined,
                    ...updates,
                })
            } catch (error) {
                console.error('Failed to update comment', error)
                throw error
            } finally {
                removeSavingCommentId(comment._id)
            }
        },
        [addSavingCommentId, removeSavingCommentId, updateCommentMutation]
    )

    const filterHandlers = useCommentsViewFilters({ filtersState, updateFilters })

    const { derivedTabStateById } = useCommentsViewDerivedData({
        comments,
        tabs: commentsViewTabs,
        selectedTabId,
        filtersState,
        dateRange: filtersState.dateRange,
        searchQuery,
        domainFilterDefinitions,
        domainTabLabels,
    })

    const handleInnerTabChange = useCallback(
        (tabId: string) => {
            const nextTabId = tabId as CommentsViewTabId
            if (controlledSelectedTabId === undefined) {
                setUncontrolledSelectedTabId(nextTabId)
            }
            onTabChange?.(nextTabId)
        },
        [controlledSelectedTabId, onTabChange]
    )

    const tabs = useMemo((): ComponentProps<typeof Tabs>['tabs'] => {
        return commentsViewTabs.map((tab) => ({
            id: tab.id,
            label: `${tab.label} (${derivedTabStateById[tab.id]?.visibleCount ?? 0})`,
            view: (
                <CommentsTableContent
                    tab={tab}
                    strategyTitle={strategyTitle}
                    viewState={derivedTabStateById[tab.id]}
                    searchQuery={searchQuery}
                    onSearchQueryChange={(value) => {
                        setSearchQuery(value)
                    }}
                    isLoading={isCommentsLoading}
                    isError={commentsQuery.isError}
                    isCommentSaving={isCommentSaving}
                    onUpdateComment={handleUpdateComment}
                    dateRange={filtersState.dateRange}
                    filterHandlers={filterHandlers}
                    hiddenColumnIds={hiddenColumnIds}
                    onHiddenColumnIdsChange={updateHiddenColumns}
                />
            ),
        }))
    }, [
        commentsQuery.isError,
        commentsViewTabs,
        derivedTabStateById,
        filterHandlers,
        filtersState.dateRange,
        handleUpdateComment,
        hiddenColumnIds,
        isCommentsLoading,
        isCommentSaving,
        searchQuery,
        strategyTitle,
        updateHiddenColumns,
    ])

    if (!strategy) {
        return null
    }

    return (
        <Fade in timeout={800}>
            <Stack
                flex={1}
                direction="column"
                sx={{
                    bgcolor: 'figma.grayscale.contentWrapper',
                }}
            >
                <Tabs
                    orientation="horizontal"
                    tabs={tabs}
                    selectedTabId={selectedTabId}
                    onTabChange={handleInnerTabChange}
                    tabPanelSx={{ px: 0, py: 4, width: '100%' }}
                    sx={{ width: '100%' }}
                />
            </Stack>
        </Fade>
    )
}

export default CommentsView
